import type { Router } from 'vue-router';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let setupDone = false;

export type GoogleAnalyticsOptions = {
  /** Sends hits to GA4 DebugView (Admin → DebugView). */
  debugMode?: boolean;
};

function trackPage(
  measurementId: string,
  pagePath: string,
  debugMode: boolean
): void {
  const opts: Record<string, unknown> = { page_path: pagePath };
  if (debugMode) {
    opts.debug_mode = true;
  }
  window.gtag('config', measurementId, opts);
}

/**
 * Load gtag.js, configure GA4, and send page views on Vue Router navigations.
 * Safe to call once; subsequent calls are ignored.
 */
export function setupGoogleAnalytics(
  measurementId: string,
  router: Router,
  options?: GoogleAnalyticsOptions
): void {
  const id = measurementId.trim();
  if (setupDone || !id) return;
  setupDone = true;

  const debugMode = Boolean(options?.debugMode);

  window.dataLayer = window.dataLayer || [];
  // Match Google's snippet: push `arguments`, not a spread array — gtag.js expects that shape.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  const initialConfig: Record<string, unknown> = { send_page_view: false };
  if (debugMode) {
    initialConfig.debug_mode = true;
  }
  window.gtag('config', id, initialConfig);
  trackPage(id, router.currentRoute.value.fullPath, debugMode);
  router.afterEach((to) => {
    trackPage(id, to.fullPath, debugMode);
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}
